import React from "react";
import { retrieveLinklyDataAsync, storeLinklyDataAsync } from "./storage";

export type TransactionSession = {
  sessionId: string;
  paymentMethodId: string;
  saleId: string;
  tenderedAmount: number;
};

const STORAGE_KEY = "linkly-transaction";

type UseLinklyPowerRecoveryReturn = {
  lastSessionInterrupted?: boolean;
  transaction?: TransactionSession;
  saveTransaction: (
    sessionId: string,
    saleId: string,
    paymentMethodId: string,
    tenderedAmount: number
  ) => Promise<void>;
  clearTransaction: () => Promise<void>;
};

export const useLinklyPowerRecovery = (): UseLinklyPowerRecoveryReturn => {
  const [transaction, setTransaction] = React.useState<
    TransactionSession | undefined
  >(undefined);

  const [lastSessionInterrupted, setLastSessionInterrupted] = React.useState<
    boolean | undefined
  >(undefined);

  React.useEffect(() => {
    (async () => {
      const data = await retrieveLinklyDataAsync(STORAGE_KEY);
      if (data) {
        setTransaction(JSON.parse(data));
        setLastSessionInterrupted(true);
      } else {
        setLastSessionInterrupted(false);
      }
    })();
  }, []);

  async function saveTransaction(
    sessionId: string,
    saleId: string,
    paymentMethodId: string,
    tenderedAmount: number
  ) {
    setLastSessionInterrupted(false);
    const transaction = {
      sessionId,
      saleId,
      paymentMethodId,
      tenderedAmount,
    };
    setTransaction(transaction);
    await storeLinklyDataAsync(STORAGE_KEY, JSON.stringify(transaction));
  }

  async function clearTransaction() {
    setTransaction(undefined);
    setLastSessionInterrupted(undefined);
    await storeLinklyDataAsync(STORAGE_KEY, null);
  }

  return {
    lastSessionInterrupted,
    transaction,
    saveTransaction,
    clearTransaction,
  };
};

export const LinklyPowerRecoveryContext =
  React.createContext<UseLinklyPowerRecoveryReturn | null>(null);

export const useLinklyPowerRecoveryContext = () =>
  React.useContext(LinklyPowerRecoveryContext) as UseLinklyPowerRecoveryReturn;

export type LinklyPowerRecoveryContextProviderProps = {
  children: React.ReactNode;
} & UseLinklyPowerRecoveryReturn;

export const LinklyPowerRecoveryContextProvider = (
  props: LinklyPowerRecoveryContextProviderProps
) => {
  const { children, ...data } = props;
  return (
    <LinklyPowerRecoveryContext.Provider
      value={data as unknown as UseLinklyPowerRecoveryReturn}
    >
      {children}
    </LinklyPowerRecoveryContext.Provider>
  );
};
